import { FunctionComponent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCssVar, ImageFile, isRemoteFileUrl } from '../../../../../shared';
import { GalleryItem } from '../../../../models';
import { CircularLoader } from '../../../CircularLoader';
import { IconLabel } from '../../../IconLabel';
import { isCroppableImageSource } from '../../../ImageCropModal';
import { SortableItem } from '../../../SortableList';

import classNames from 'classnames';
import classes from '../FilePreview.module.scss';

interface FilePreviewTeaserProps {
  item: GalleryItem;
  index: number;
  isEditable: boolean;
  editableMedia: boolean;
  hasSettings: boolean;
  loading: boolean;
  isCropping: boolean;
  isSettingsActive: boolean;
  isImageDraggable: boolean;
  onPreviewOpen: (url: string) => void;
  onCropOpen: (image: ImageFile) => void;
  onSettingsOpen: (index: number) => void;
  onRemove: (index: number) => void;
}

const getFileType = ({ audio, video, pdf }: GalleryItem) => {
  if (audio) {
    return 'audio';
  }

  if (video) {
    return 'video';
  }

  if (pdf) {
    return 'pdf';
  }
};

export const FilePreviewTeaser: FunctionComponent<FilePreviewTeaserProps> = memo(
  ({
    item,
    index,
    isEditable,
    editableMedia,
    hasSettings,
    loading,
    isCropping,
    isSettingsActive,
    isImageDraggable,
    onPreviewOpen,
    onCropOpen,
    onSettingsOpen,
    onRemove,
  }) => {
    const { t } = useTranslation();

    const { image, audio, video, pdf } = item;

    const file = audio ?? video ?? pdf;

    const fileType = getFileType(item);

    const { id: fileId, url: fileUrl } = { ...file };

    const { id: imageId, url: imageUrl, originalFilename } = { ...image };

    const id = fileId ?? imageId;

    if (!id) {
      return null;
    }

    const mediaUrl = fileUrl ?? imageUrl;

    const canOpenMedia = isRemoteFileUrl(mediaUrl);

    const isImageItem = !fileType;

    const isCroppableImage = Boolean(
      imageUrl && isCroppableImageSource(originalFilename || imageUrl)
    );

    const canCrop = Boolean(isEditable && editableMedia && isImageItem && isCroppableImage);

    return (
      <SortableItem
        id={id}
        disabled={!isImageItem || isCropping}
        className={classNames(classes['file-preview__teaser'], {
          [classes['file-preview__teaser--loading']]: loading || isCropping,
        })}
      >
        <div
          className={classNames(classes['file-preview__teaser-url'], {
            [classes['file-preview__teaser-url--overlay']]: Boolean(fileType),
            [classes['file-preview__teaser-url--pointer']]: imageUrl,
            [classes['file-preview__teaser-url--move']]: isImageDraggable && isImageItem,
          })}
          {...(imageUrl && {
            style: { backgroundImage: `url('${imageUrl}')` },
            onClick: () => onPreviewOpen(imageUrl),
          })}
        ></div>

        {fileType && (
          <IconLabel
            iconId={fileType}
            iconSize={32}
            className={classes['file-preview__teaser-icon']}
            color={'#ffffff'}
            singleColor
            nonClickable
          />
        )}

        {isCropping && (
          <div className={classes['file-preview__teaser-loader']}>
            <CircularLoader sizeRem={2} />
          </div>
        )}

        {(canOpenMedia || canCrop) && (
          <div
            className={classNames(
              classes['file-preview__actions'],
              classes['file-preview__actions--left']
            )}
          >
            {canOpenMedia && (
              <IconLabel
                className={classes['file-preview__actions-link']}
                iconId={'arrow-rounded-right-up-box'}
                iconSize={18}
                ariaLabel={t('common.open-in-new-tab')}
                color={getCssVar('--base-link-text-color')}
                hoverColor={getCssVar('--base-link-text-hover-color')}
                onClick={() => window.open(mediaUrl, '_blank', 'noopener, noreferrer')}
              />
            )}

            {canCrop && image && (
              <IconLabel
                className={classes['file-preview__actions-crop']}
                iconId={'crop'}
                iconSize={18}
                ariaLabel={t('imageCrop.title')}
                onClick={() => onCropOpen(image)}
              />
            )}
          </div>
        )}

        {isEditable && (
          <div className={classes['file-preview__actions']}>
            {hasSettings && (
              <IconLabel
                className={classNames(classes['file-preview__actions-settings'], {
                  [classes['file-preview__actions-settings--active']]: isSettingsActive,
                })}
                iconId={'dots-menu'}
                iconSize={30}
                ariaLabel={t('common.settings')}
                onClick={() => onSettingsOpen(index)}
              />
            )}

            {editableMedia && (
              <span
                className={classes['file-preview__actions-remove']}
                onClick={() => onRemove(index)}
              ></span>
            )}
          </div>
        )}
      </SortableItem>
    );
  }
);
